import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import api from '../../utils/api';
import ProductCard from './ProductCard';
import { getPageCache, setPageCache } from '../../utils/pageCache';

const PRICE_RANGES = [
  { id: 1, label: 'Budget Buys', name: 'Under ₹199', min: 0, max: 199, emoji: '💰' },
  { id: 2, label: 'Great Deals', name: '₹200 - ₹499', min: 200, max: 499, emoji: '💵' },
  { id: 3, label: 'Premium Choice', name: '₹500 - ₹999', min: 500, max: 999, emoji: '💴' },
  { id: 4, label: 'Luxury Items', name: 'Above ₹1000', min: 1000, max: 99999, emoji: '💎' },
];

function SectionHeader({ label, title, viewAllHref }) {
  return (
    <div className="flex items-end justify-between mb-5">
      <div>
        {label && <p className="text-xs font-black text-brand-yellow uppercase tracking-widest mb-1">{label}</p>}
        <h2 className="section-heading">{title}</h2>
      </div>
      {viewAllHref && (
        <Link to={viewAllHref} className="text-sm font-semibold text-brand-dark hover:text-brand-yellow transition-colors flex items-center gap-1">
          View All <ChevronRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

export default function PriceCategories() {
  // Hydrate from cache on Back/Forward so this (otherwise lazy) section renders
  // at full height immediately. Without it the section loads in late and pushes
  // the rows below it down — which broke scroll restoration (the card you
  // clicked drifted away after you'd already returned to it).
  const cached = getPageCache('priceCategories');
  const [priceCategories, setPriceCategories] = useState(cached || {});
  const [visible, setVisible] = useState(!!cached);
  const ref = useRef(null);

  // These 4 requests are the heaviest on the homepage, so don't fire them on
  // initial load. Wait until the section is about to scroll into view (300px
  // before) — then it still feels instant but never competes with the
  // above-the-fold content.
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { rootMargin: '300px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    // Fetch products for each price range
    Promise.allSettled(
      PRICE_RANGES.map(range =>
        api.get('/products', {
          params: {
            limit: 12,
            priceMin: range.min,
            priceMax: range.max,
            sort: 'createdAt',
          }
        })
      )
    ).then(results => {
      const categoriesData = {};
      results.forEach((result, idx) => {
        const range = PRICE_RANGES[idx];
        if (result.status === 'fulfilled' && result.value.data.success) {
          categoriesData[range.id] = result.value.data.products || [];
        } else {
          categoriesData[range.id] = [];
        }
      });
      setPriceCategories(categoriesData);
      setPageCache('priceCategories', categoriesData);
    });
  }, [visible]);

  return (
    <div ref={ref}>
      {PRICE_RANGES.map(range => {
        const products = priceCategories[range.id] || [];
        if (products.length === 0) return null;

        return (
          <section key={range.id} className="max-w-7xl mx-auto px-4 py-6">
            <SectionHeader 
              label={range.label} 
              title={range.name}
              viewAllHref={`/products?priceMin=${range.min}&priceMax=${range.max}`}
            />
            <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2">
              {products.map(product => (
                <div key={product.id} className="flex-shrink-0 w-44 md:w-52">
                  <ProductCard product={product} rowId={range.name} />
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
